import * as React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip,
  Label,
  TooltipProps,
} from 'recharts';
import { CircularProgress } from '@mui/material';
import { HistoricalPerDiemCalendarPeriod } from 'pages/Shipments/HistoricalPerDiem/params';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { DayBreakdown, Breakdown } from './types';
import { Colors } from '../../colors';
import { getHatchPatternFill, HatchPatternId } from '../../Patterns';
import { HoverTooltip } from './HoverTooltip';
import { formatDateWithCalendarPeriod, formatDollars, formatGetWeekRange } from './utility';

interface Props {
  data: DayBreakdown[];
  showAverage: boolean;
  showHistoric: boolean;
  showHistoricAverage: boolean;
  isLoading: boolean;
  calendarPeriod: HistoricalPerDiemCalendarPeriod;
}

enum StackId {
  Current = 'current',
  Comparison = 'comparison',
}

export const Graph: React.FC<Props> = (props) => {
  const { data, showHistoric, showAverage, showHistoricAverage, isLoading, calendarPeriod } = props;

  const noData = data.length === 0;

  const averages = React.useMemo(() => {
    const current = data.filter((e) => !!e.current).map((e) => e.current) as Breakdown[];
    const historic = data.filter((e) => !!e.historic).map((e) => e.historic) as Breakdown[];

    // return the average of the sum of disputable and nonDisputable
    return {
      current:
        current.length > 0
          ? current.reduce((acc, curr) => acc + curr.disputable + curr.nonDisputable, 0) /
            current.length
          : 0,
      comparison:
        historic.length > 0
          ? historic.reduce((acc, curr) => acc + curr.disputable + curr.nonDisputable, 0) /
            historic.length
          : 0,
    };
  }, [data]);

  const formatDateLabel = React.useCallback(
    (date: string) => {
      return formatDateWithCalendarPeriod(date, calendarPeriod);
    },
    [calendarPeriod]
  );

  const tooltipContent = React.useCallback(
    (tooltipProps: TooltipProps<ValueType, NameType>) =>
      HoverTooltip({ tooltipProps, showHistoric }),
    [showHistoric]
  );

  return (
    <div className="w-full h-[400px] relative">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          width={500}
          height={500}
          barGap={8}
          barCategoryGap={8}
          margin={{ left: 10, bottom: 10 }}
        >
          {noData ? null : (
            <>
              <CartesianGrid
                horizontal={false}
                vertical
                verticalCoordinatesGenerator={(p) => {
                  const count: number = (p.xAxis.domain as string[]).length;
                  const { width } = p.xAxis;
                  const step: number = width / count;
                  const offset: number = p.offset.left;
                  // create an array that is count length and fill it with the step times the index
                  return Array.from({ length: count }, (_, i) => (i + 1) * step + offset);
                }}
                stroke={Colors.Grid}
              />
              <Tooltip cursor={{ fill: Colors.HoverBackground }} content={tooltipContent} />
              <XAxis
                tickCount={1}
                dataKey="date"
                tickFormatter={formatDateLabel}
                stroke={Colors.Line}
                tick={{ fill: Colors.Text }}
                tickLine={false}
              />
              {calendarPeriod === HistoricalPerDiemCalendarPeriod.Week ? (
                <XAxis
                  tickCount={1}
                  dataKey="date"
                  stroke={Colors.Line}
                  tick={{ fill: Colors.Text }}
                  tickFormatter={formatGetWeekRange}
                  tickLine={false}
                  axisLine={false}
                  xAxisId="calendarWeek"
                  capHeight={0}
                  height={4}
                />
              ) : null}
              <YAxis
                tickCount={6}
                tickFormatter={formatDollars}
                stroke={Colors.Line}
                tick={{ fill: Colors.Text }}
                tickLine
              />
              <Bar
                dataKey="current.nonDisputable"
                stackId={StackId.Current}
                fill={Colors.NonDisputable}
                // https://github.com/recharts/recharts/issues/829
                isAnimationActive={false}
              />
              <Bar
                dataKey="current.disputable"
                stackId={StackId.Current}
                fill={Colors.Disputable}
                radius={[4, 4, 0, 0]}
                // https://github.com/recharts/recharts/issues/829
                isAnimationActive={false}
              />
              {showHistoric && (
                <>
                  <Bar
                    dataKey="historic.nonDisputable"
                    stackId={StackId.Comparison}
                    fill={getHatchPatternFill(HatchPatternId.NonDisputable)}
                  />
                  <Bar
                    dataKey="historic.disputable"
                    stackId={StackId.Comparison}
                    fill={getHatchPatternFill(HatchPatternId.Disputable)}
                    radius={[4, 4, 0, 0]}
                  />
                </>
              )}
              {showAverage && (
                <ReferenceLine y={averages.current} stroke="#E59535" strokeWidth={2}>
                  <Label value={formatDollars(averages.current)} position="left" />
                </ReferenceLine>
              )}
              {showHistoric && showHistoricAverage && (
                <ReferenceLine
                  y={averages.comparison}
                  stroke="#E59535"
                  strokeWidth={2}
                  strokeDasharray="3 3"
                >
                  <Label value={formatDollars(averages.comparison)} position="left" />
                </ReferenceLine>
              )}
            </>
          )}
        </BarChart>
      </ResponsiveContainer>
      {isLoading ? (
        <div className="flex items-center justify-center absolute inset-0 pointer-events-none">
          <CircularProgress />
        </div>
      ) : null}
      {noData && !isLoading ? (
        <div className="flex items-center justify-center absolute inset-0 pointer-events-none">
          <h5 className="text-gray-400">No data</h5>
        </div>
      ) : null}
    </div>
  );
};
Graph.displayName = 'Shipments.Historical.ChargesOverTime.Graph';
